import { inject, Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Subject } from 'rxjs';

import { environment } from '@cure-environment/environment';
import { StorageService } from '@cure-services/storage';

export type SocketConnectionStatus = 'connected' | 'disconnected' | 'error';

export interface AdminOrderSocketEvent {
  type: 'new' | 'update';
  data: unknown;
}

@Injectable()
export class AdminOrderSocketService implements OnDestroy {
  private readonly storageService = inject(StorageService);

  private socket: Socket | null = null;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionErrorTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly CONNECTION_ERROR_THRESHOLD_MS = 60_000;

  private readonly _status$ = new BehaviorSubject<SocketConnectionStatus>('disconnected');
  readonly status$ = this._status$.asObservable();

  private readonly _orderEvent$ = new Subject<AdminOrderSocketEvent>();
  readonly orderEvent$ = this._orderEvent$.asObservable();

  private readonly _restFallback$ = new Subject<void>();
  /** Emits once whenever a reconnection restores the socket — caller should re-fetch data. */
  readonly restFallback$ = this._restFallback$.asObservable();

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const token = this.storageService.getToken();
    const url = this.getSocketUrl();

    this.socket = io(`${url}/admin`, {
      transports: ['websocket'],
      auth: { token },
      extraHeaders: { Authorization: `Bearer ${token}` },
      autoConnect: true,
      reconnection: false, // manual reconnect with backoff
    });

    this.socket.on('connect', () => this.onConnected());
    this.socket.on('disconnect', () => this.onDisconnected());
    this.socket.on('connect_error', () => this.onError());
    this.socket.on('orders', (type: 'new' | 'update', payload: { data: unknown }) => {
      this._orderEvent$.next({ type, data: payload.data });
    });
  }

  disconnect(): void {
    this.clearTimers();
    this.socket?.disconnect();
    this.socket = null;
    this._status$.next('disconnected');
  }

  subscribeToOrder(orderId: string): void {
    this.socket?.emit('subscribe-order', { orderId });
  }

  unsubscribeFromOrder(orderId: string): void {
    this.socket?.emit('unsubscribe-order', { orderId });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private onConnected(): void {
    this.reconnectAttempts = 0;
    this.clearTimers();
    const wasError = this._status$.value === 'error';
    this._status$.next('connected');
    if (wasError) {
      this._restFallback$.next();
    }
  }

  private onDisconnected(): void {
    this._status$.next('disconnected');
    this.scheduleReconnect();
  }

  private onError(): void {
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      this._status$.next('error');
      return;
    }

    // Start error timer on first failure
    if (!this.connectionErrorTimer) {
      this.connectionErrorTimer = setTimeout(() => {
        this._status$.next('error');
      }, this.CONNECTION_ERROR_THRESHOLD_MS);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30_000);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.socket?.connect();
    }, delay);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectionErrorTimer) {
      clearTimeout(this.connectionErrorTimer);
      this.connectionErrorTimer = null;
    }
  }

  private getSocketUrl(): string {
    return environment.apiUrl;
  }
}
